import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { MessageService } from '@openng/optimus-ui/api';
import { Optimus } from '@openng/optimus-ui/config';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { GraphQlService } from '~ngx-shared/graph-ql';
import { LayoutConfig, LayoutService, LoadingService, MenuService } from '~ngx-shared/layout';
import { FileService, VersionService } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { UserService } from '~shared/services';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';
import { Finance } from './theme/finance';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly versionService = inject(VersionService);
  private readonly messageService = inject(MessageService);
  private readonly optimusConfig = inject(Optimus);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly graphQlService = inject(GraphQlService);

  constructor() {
    const authenticationService = inject(AuthenticationService);
    const userService = inject(UserService);
    const fileService = inject(FileService);

    fileService.init(
      `${environment.externalServiceUrl}/file/upload`,
      `${environment.externalServiceUrl}/file`
    );

    effect(() => {
      const isNewAvailable = this.versionService.isNewVersionAvailable();
      if (isNewAvailable) {
        this.messageService.add({
          severity: 'error',
          summary: 'update',
          closable: false,
          sticky: true,
          key: 'new_version_available'
        });
      }
    });

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        authenticationService.init(
          ['default-roles-iggoe', 'iggoe-user'],
          url =>
            url.startsWith(environment.hasuraUrl) || url.startsWith(environment.externalServiceUrl)
        );
        this.authorizationService.setAllowedRoles(
          Object.values(AuthorizationUtil.GROUP_ACCOUNTANT)
        );
        userService.init();

        this.menuService.sideBarItems$.next([
          {
            label: 'Home',
            items: [
              {
                label: 'dashboard',
                icon: 'pi pi-fw pi-home',
                routerLink: ['/', 'dashboard']
              },
              {
                label: 'profile',
                icon: 'pi pi-fw pi-user',
                routerLink: ['/', 'profile']
              }
            ]
          },
          {
            label: 'shared.finance',
            items: [
              {
                label: 'finance.account_plan',
                routerLink: ['/', 'account-plan'],
                icon: 'pi pi-fw pi-book'
              },
              {
                label: 'finance.entries',
                routerLink: ['/', 'entries'],
                icon: 'pi pi-fw pi-arrows-h'
              },
              {
                label: 'shared.bills',
                routerLink: ['/', 'bills'],
                icon: 'pi pi-fw pi-money-bill'
              },
              {
                label: 'shared.solidarity_contribution_bills',
                routerLink: ['/', 'solidarity-contribution-bills'],
                icon: 'pi pi-fw pi-money-bill'
              },
              {
                label: 'finance.camt_import',
                routerLink: ['/', 'camt-import'],
                icon: 'pi pi-fw pi-file-import'
              },
              {
                label: 'finance.tax_office',
                routerLink: ['/', 'tax-office'],
                icon: 'pi pi-euro'
              },
              {
                label: 'finance.accounting_period',
                routerLink: ['/', 'period'],
                icon: 'pi pi-fw pi-cog'
              },
              {
                label: 'finance.accounting_periods',
                routerLink: ['/', 'periods'],
                icon: 'pi pi-fw pi-calendar'
              }
            ]
          },
          {
            label: 'shared.communities',
            items: [
              {
                label: 'shared.community_type.religious_communities',
                routerLink: ['/', 'communities', 'religious-communities'],
                icon: 'pi pi-fw pi-building-columns'
              },
              {
                label: 'shared.community_type.cultic_communities',
                routerLink: ['/', 'communities', 'cultic-communities'],
                icon: 'pi pi-fw pi-building-columns',
                items: [
                  {
                    label: 'shared.community_type.mosque_facilities',
                    routerLink: ['/', 'communities', 'mosque-facilities'],
                    icon: 'pi pi-fw pi-building-columns'
                  }
                ]
              },
              {
                label: 'shared.community_type.mosques',
                routerLink: ['/', 'communities', 'mosques'],
                icon: 'pi pi-fw pi-building-columns'
              },
              {
                label: 'shared.community_type.professional_associations',
                routerLink: ['/', 'communities', 'professional-associations'],
                icon: 'pi pi-fw pi-building-columns'
              },
              {
                label: 'shared.community_type.prayer_rooms',
                routerLink: ['/', 'communities', 'prayer-rooms'],
                icon: 'pi pi-fw pi-building-columns'
              },
              {
                label: 'shared.communities',
                routerLink: ['/', 'communities', 'all'],
                icon: 'pi pi-fw pi-building-columns'
              }
            ]
          },
          {
            label: 'shared.actors',
            items: [
              {
                label: 'shared.role.members',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'members']
              },
              {
                label: 'shared.role.teachers',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'teachers']
              },
              {
                label: 'shared.role.inspectors',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'inspectors']
              },
              {
                label: 'shared.role.accountants',
                icon: 'pi pi-fw pi-briefcase',
                routerLink: ['/', 'actors', 'accountants'],
                state: { permissions: RoleEnum.MODERATOR }
              },
              {
                label: 'shared.people',
                icon: 'pi pi-fw pi-users',
                routerLink: ['/', 'actors', 'people']
              }
            ]
          },
          {
            label: 'shared.administration',
            items: [
              {
                label: 'shared.finance',
                icon: 'pi pi-fw pi-money-bill',
                routerLink: ['/', 'administration', 'finance']
              },
              {
                label: 'shared.actors',
                icon: 'pi pi-fw pi-users',
                routerLink: ['/', 'administration', 'actors']
              },
              {
                label: 'shared.print_templates',
                icon: 'pi pi-fw pi-file-pdf',
                routerLink: ['/', 'print-templates'],
                state: { permissions: AuthorizationUtil.GROUP_ACCOUNTANT }
              }
            ]
          }
        ]);
      }
    });
  }

  ngOnInit(): void {
    this.loadingService.startLoading();

    //optional configuration with the default configuration
    const config: LayoutConfig = {
      preset: 'Aura',
      primary: 'violet',
      surface: null,
      darkTheme: false,
      menuMode: 'static'
    };
    this.layoutService.layoutConfig.set(config);
    this.optimusConfig.theme.set({
      ...this.optimusConfig.theme(),
      preset: Finance
    });

    this.translocoService.selectTranslateObject('primeng').subscribe(translated => {
      this.optimusConfig.setTranslation(translated);
    });

    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(role => {
        if (role) {
          this.graphQlService.init(() => role);
        }
      });

    this.loadingService.stopLoading();
  }
}
